import * as React from "react"
import {cva, type VariantProps} from "class-variance-authority"
import {cn} from "@/lib/utils"

const badgeVariants = cva(
    "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
    {
        variants: {
            variant: {
                default:
                    "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
                secondary:
                    "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
                destructive:
                    "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
                outline: "text-foreground",
                success:
                    "border-transparent bg-success text-success-foreground hover:bg-success/80",
                warning:
                    "border-transparent bg-warning text-warning-foreground hover:bg-warning/80",
                danger:
                    "border-transparent bg-danger text-danger-foreground hover:bg-danger/80",
                count:
                    "border-transparent text-xs font-normal bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/20 hover:text-foreground tabular-nums",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
)

export interface BadgeProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof badgeVariants> {
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
    ({className, variant, ...props}, ref) => {
        return (
            <div ref={ref} className={cn(badgeVariants({variant}), className)} {...props} />
        )
    }
)
Badge.displayName = "Badge"

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const BadgeVariant = {
    DEFAULT: 'default',
    SECONDARY: 'secondary',
    DESTRUCTIVE: 'destructive',
    OUTLINE: 'outline',
    SUCCESS: 'success',
    WARNING: 'warning',
    DANGER: 'danger',
    COUNT: 'count',
} as const;
export type BadgeVariant = typeof BadgeVariant[keyof typeof BadgeVariant];
/* eslint-enable i18next/no-literal-string */

export {Badge, badgeVariants}
